/**
 * Klaviyo Direct API Fallbacks
 *
 * The CDN's Klaviyo wrapper functions have a bug where they reference bare
 * variables (collection, taxonomy) without destructuring them from the input.
 * These fallback functions call window.klaviyo directly when CDN functions fail.
 */

import type { WindowAnalyzify } from '../../types/window-analyzify';

// Matches an_klaviyo.js `const eventSource = 'analyzify_extension'`.
// Emitted as `source` (and `$event_id`) so Klaviyo can dedup against
// server-side events, matching the CDN payload shape.
const KLAVIYO_EVENT_SOURCE = 'analyzify_extension';

/**
 * Get Klaviyo product ID based on configured format
 */
export function getKlaviyoProductId(analyzify: WindowAnalyzify, product: any, variant: any): string {
  const format = analyzify.properties?.KLAVIYO?.product_id_format || 'product_id';
  // Match CDN's getIdByFormat which checks product_id/variant_id fields first,
  // falling back to .id. This matters for cart items where .id is the line item ID.
  switch (format) {
    case 'variant_id':
      return product?.variant_id?.toString() || variant?.id?.toString() || product?.variants?.[0]?.id?.toString() || '';
    case 'product_sku':
      return variant?.sku?.toString() || product?.sku?.toString() || '';
    case 'shopify_item_id': {
      const productId = product?.product_id?.toString() || product?.id?.toString() || '';
      const variantId = product?.variant_id?.toString() || variant?.id?.toString() || product?.variants?.[0]?.id?.toString() || '';
      return `shopify_${analyzify.feed_region || 'US'}_${productId}_${variantId}`;
    }
    default: // 'product_id'
      return product?.product_id?.toString() || product?.id?.toString() || '';
  }
}

/**
 * Direct Klaviyo API: Added to Cart
 */
export function trackKlaviyoAddToCartDirect(analyzify: WindowAnalyzify, product: any, variant: any, quantity: number): void {
  const klaviyo = (window as any).klaviyo;
  if (!klaviyo) return;

  const props = analyzify.properties?.KLAVIYO;
  if (!props?.status || !props.events?.add_to_cart) return;

  const price = (variant.price || 0) / 100;
  const productId = getKlaviyoProductId(analyzify, product, variant);

  try {
    const currency = analyzify.currency || '';
    const eventId = analyzify.eventId?.get('add_to_cart');
    const klvyObj = {
      $value: price * quantity,
      AddedItemProductName: product.title,
      AddedItemProductID: productId,
      AddedItemSKU: variant.sku?.toString(),
      AddedItemURL: typeof window !== 'undefined' ? window.location.href : '',
      AddedItemPrice: price,
      AddedItemQuantity: quantity,
      Name: product.title,
      ProductID: productId,
      Items: [{
        ProductName: product.title,
        SKU: variant.sku?.toString(),
        URL: typeof window !== 'undefined' ? window.location.href : '',
        Categories: [product.type].filter(Boolean),
        Brand: product.vendor,
        Price: price,
        CompareAtPrice: (variant.compare_at_price || 0) / 100
      }],
      $event_id: eventId,
      source: KLAVIYO_EVENT_SOURCE,
    };
    klaviyo.track("Added to Cart", klvyObj);
    klaviyo.track("Added to Cart - Analyzify", {
      ...klvyObj,
      $value_currency: currency,
      analyzify_price: `${currency}${Number(price * quantity) || 0}`,
    });
  } catch (error) {
    console.warn('[Analyzify] Klaviyo direct API error:', error);
  }
}

/**
 * Direct Klaviyo API: Viewed Product
 */
export function trackKlaviyoViewItemDirect(analyzify: WindowAnalyzify, product: any, variant: any): void {
  const klaviyo = (window as any).klaviyo;
  if (!klaviyo) return;

  const props = analyzify.properties?.KLAVIYO;
  if (!props?.status || !props.events?.view_item) return;

  const price = (variant.price || 0) / 100;
  const productId = getKlaviyoProductId(analyzify, product, variant);

  try {
    const eventId = analyzify.eventId?.get('view_item');
    const klvyObj = {
      ProductName: product.title,
      ProductID: productId,
      SKU: variant.sku?.toString(),
      Categories: [product.type].filter(Boolean),
      URL: typeof window !== 'undefined' ? window.location.href : '',
      Brand: product.vendor,
      Price: price,
      CompareAtPrice: (variant.compare_at_price || 0) / 100,
      $event_id: eventId,
      source: KLAVIYO_EVENT_SOURCE,
    };

    const currency = analyzify.currency || '';
    const viewedPageObj = { PageName: product.title, $event_id: eventId, source: KLAVIYO_EVENT_SOURCE };

    // Track page view (both the legacy event name and the Analyzify-suffixed variant)
    klaviyo.track("Viewed Page", viewedPageObj);
    klaviyo.track("Viewed Page - Analyzify", viewedPageObj);

    // Track product view
    klaviyo.track("Viewed Product", klvyObj);
    klaviyo.track("Viewed Product - Analyzify", {
      ...klvyObj,
      $value: price,
      $value_currency: currency,
      analyzify_price: `${currency}${Number(price) || 0}`,
    });

    // Track for catalog (back in stock, etc.)
    if (typeof klaviyo.trackViewedItem === 'function') {
      klaviyo.trackViewedItem({
        Title: klvyObj.ProductName,
        ItemId: klvyObj.ProductID,
        Categories: klvyObj.Categories,
        Url: klvyObj.URL,
        Metadata: {
          Brand: klvyObj.Brand,
          Price: klvyObj.Price,
          CompareAtPrice: klvyObj.CompareAtPrice,
          source: KLAVIYO_EVENT_SOURCE
        }
      });
    }
  } catch (error) {
    console.warn('[Analyzify] Klaviyo direct API error:', error);
  }
}

/**
 * Direct Klaviyo API: Viewed Category
 */
export function trackKlaviyoViewCategoryDirect(analyzify: WindowAnalyzify, collectionTitle: string): void {
  const klaviyo = (window as any).klaviyo;
  if (!klaviyo) return;

  const props = analyzify.properties?.KLAVIYO;
  if (!props?.status || !props.events?.view_item_list) return;

  try {
    const eventId = analyzify.eventId?.get('view_item_list');
    const viewedPageObj = { PageName: collectionTitle, $event_id: eventId, source: KLAVIYO_EVENT_SOURCE };
    const viewedCategoryObj = { CategoryName: collectionTitle, $event_id: eventId, source: KLAVIYO_EVENT_SOURCE };
    klaviyo.track("Viewed Page", viewedPageObj);
    klaviyo.track("Viewed Page - Analyzify", viewedPageObj);
    klaviyo.track("Viewed Category", viewedCategoryObj);
    klaviyo.track("Viewed Collection - Analyzify", viewedCategoryObj);
  } catch (error) {
    console.warn('[Analyzify] Klaviyo direct API error:', error);
  }
}

/**
 * Direct Klaviyo API: Searched Site
 */
export function trackKlaviyoSearchDirect(analyzify: WindowAnalyzify, term: string, resultsCount: number): void {
  const klaviyo = (window as any).klaviyo;
  if (!klaviyo) return;

  const props = analyzify.properties?.KLAVIYO;
  if (!props?.status || !props.events?.search) return;

  try {
    const eventId = analyzify.eventId?.get('search');
    const searchedSiteObj = {
      SearchTerm: term,
      ReturnedResults: resultsCount,
      $event_id: eventId,
      source: KLAVIYO_EVENT_SOURCE,
    };
    klaviyo.track("Searched Site", searchedSiteObj);
    klaviyo.track("Searched Site - Analyzify", searchedSiteObj);
    const viewedPageObj = { PageName: "Search Results: " + term, $event_id: eventId, source: KLAVIYO_EVENT_SOURCE };
    klaviyo.track("Viewed Page", viewedPageObj);
    klaviyo.track("Viewed Page - Analyzify", viewedPageObj);
  } catch (error) {
    console.warn('[Analyzify] Klaviyo direct API error:', error);
  }
}

/**
 * Direct Klaviyo API: Started Checkout
 */
export function trackKlaviyoBeginCheckoutDirect(analyzify: WindowAnalyzify, cart: any): void {
  const klaviyo = (window as any).klaviyo;
  if (!klaviyo) return;

  const props = analyzify.properties?.KLAVIYO;
  if (!props?.status || !props.events?.begin_checkout) return;
  if (!cart?.items?.length) return;

  try {
    const klvyItems = cart.items.map((item: any) => ({
      ProductName: item.product_title,
      ProductID: getKlaviyoProductId(analyzify, item, item),
      SKU: item.sku?.toString(),
      ProductCategories: [item.product_type].filter(Boolean),
      ItemPrice: analyzify.formatPrice?.(item.price) || item.price / 100,
      Quantity: item.quantity,
    }));

    const startedCheckoutObj = {
      ItemNames: klvyItems.map((item: any) => item.ProductName),
      Items: klvyItems,
      $event_id: analyzify.eventId?.get('begin_checkout'),
      source: KLAVIYO_EVENT_SOURCE,
    };
    const currency = analyzify.currency || '';
    const cartValue = analyzify.formatPrice?.(cart.total_price, true) ?? (cart.total_price / 100);
    klaviyo.track("Started Checkout", startedCheckoutObj);
    klaviyo.track("Started Checkout - Analyzify", {
      ...startedCheckoutObj,
      $value: cartValue,
      $value_currency: currency,
      analyzify_price: `${currency}${Number(cartValue) || 0}`,
    });
  } catch (error) {
    console.warn('[Analyzify] Klaviyo direct API error:', error);
  }
}

/**
 * Direct Klaviyo API: Added to Wishlist
 */
export function trackKlaviyoAddToWishlistDirect(analyzify: WindowAnalyzify, product: any, variant: any): void {
  const klaviyo = (window as any).klaviyo;
  if (!klaviyo) return;

  const props = analyzify.properties?.KLAVIYO;
  if (!props?.status || !props.events?.add_to_wishlist) return;

  const price = (variant.price || 0) / 100;
  const productId = getKlaviyoProductId(analyzify, product, variant);

  try {
    const klvyObj = {
      $value: price,
      source: KLAVIYO_EVENT_SOURCE,
      Items: [{
        ProductName: product.title,
        ProductID: productId,
        SKU: variant.sku?.toString(),
        URL: typeof window !== 'undefined' ? window.location.href : '',
        Price: price,
        CompareAtPrice: (variant.compare_at_price || 0) / 100,
        Categories: [product.type].filter(Boolean),
        Brand: product.vendor
      }]
    };

    const currency = analyzify.currency || '';
    klaviyo.push(["track", "Added to Wishlist", klvyObj]);
    klaviyo.track("Added to Wishlist", klvyObj);
    klaviyo.track("Added to Wishlist - Analyzify", {
      ...klvyObj,
      $value_currency: currency,
      analyzify_price: `${currency}${Number(price) || 0}`,
    });
  } catch (error) {
    console.warn('[Analyzify] Klaviyo direct API error:', error);
  }
}
